// XSM-1536 - SSO Login flow
// Author: Abhishek Bharti
// withAuth.tsx
import { useState, useEffect, useCallback, useRef, ComponentType } from "react";
import useLogout from "../hooks/useLogout";
import { decodeJwtWithJsrs } from "../utils/helpers";
import {
  refreshAccessToken,
  REFRESH_THRESHOLD_MS,
} from "../utils/tokenRefresh";

// Check token expiry every 30 seconds
const CHECK_INTERVAL_MS = 30000;

// Define the HOC type
const withAuth = <P extends object>(WrappedComponent: ComponentType<P>) => {
  return function WithAuth(props: P) {
    const [isActive, setIsActive] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const refreshTokenRef = useRef(localStorage.getItem("refresh_token") || "");
    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    const { handleLogout } = useLogout();
    const isAuthenticated = localStorage.getItem("access_token");

    const logoutUser = useCallback(() => {
      handleLogout();
    }, [handleLogout]);

    const refreshToken = useCallback(async () => {
      // Skip if a refresh is already running in this component instance
      if (isRefreshing) return false;
      setIsRefreshing(true);

      try {
        const refreshed = await refreshAccessToken();
        if (refreshed) {
          // Keep the in-memory ref in sync with the rotated refresh token
          refreshTokenRef.current =
            localStorage.getItem("refresh_token") || "";
        }
        return refreshed;
      } catch (error) {
        return false;
      } finally {
        setIsRefreshing(false);
      }
    }, [isRefreshing]);

    const checkTokenStatus = useCallback(() => {
      let storedExpiry = Number(localStorage.getItem("expires_in"));

      // Derive expiry from the JWT when localStorage value is missing or invalid
      if (!storedExpiry && isAuthenticated) {
        const decodedToken = decodeJwtWithJsrs(isAuthenticated);
        if (decodedToken?.exp) {
          storedExpiry = decodedToken.exp * 1000;
        }
      }

      if (!isAuthenticated || !storedExpiry) {
        return;
      }

      const currentTime = Date.now();
      const timeLeft = storedExpiry - currentTime;

      const minutesLeft = Math.floor(timeLeft / 60000);
      const secondsLeft = Math.floor((timeLeft % 60000) / 1000);

      console.log(`Token status - ${minutesLeft}m ${secondsLeft}s remaining`, {
        expiresAt: new Date(storedExpiry).toLocaleTimeString(),
        currentTime: new Date(currentTime).toLocaleTimeString(),
      });

      if (timeLeft <= 0) {
        // Token already expired — try silent refresh before logging the user out
        if (isActive && refreshTokenRef.current) {
          console.log("Token expired - attempting silent refresh...");
          refreshToken();
          return;
        }
        console.log("Token expired - logging out");
        logoutUser();
      } else if (timeLeft <= REFRESH_THRESHOLD_MS && isActive) {
        // Token is about to expire — refresh in the background so work is not blocked
        console.log(
          `Token about to expire (${minutesLeft}m ${secondsLeft}s left) - refreshing...`
        );
        refreshToken();
      }
    }, [isActive, isAuthenticated, refreshToken, logoutUser]);

    useEffect(() => {
      if (isAuthenticated) {
        // Run an immediate check so near-expiry sessions refresh on page load
        checkTokenStatus();

        // Periodically check token status and refresh before expiry
        intervalRef.current = setInterval(checkTokenStatus, CHECK_INTERVAL_MS);
      }

      return () => {
        if (intervalRef.current) clearInterval(intervalRef.current);
      };
    }, [isAuthenticated, checkTokenStatus]);

    useEffect(() => {
      const handleVisibilityChange = () => {
        const isNowActive = document.visibilityState === "visible";
        setIsActive(isNowActive);

        // When the tab becomes visible again, refresh immediately if the token is close to expiry
        if (isNowActive && isAuthenticated) {
          checkTokenStatus();
        }
      };

      document.addEventListener("visibilitychange", handleVisibilityChange);
      return () => {
        document.removeEventListener(
          "visibilitychange",
          handleVisibilityChange
        );
      };
    }, [isAuthenticated, checkTokenStatus]);

    if (!isAuthenticated) {
      return null;
    }

    return <WrappedComponent {...props} />;
  };
};

export default withAuth;
